import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, Image, Folder, X, Search, Check, Plus, CheckSquare, ArrowUpDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { useClient } from '../context/ClientContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper: fetch with auto-refresh on 401
async function authFetch(url, options = {}) {
  let resp = await fetch(url, options);
  if (resp.status === 401) {
    try {
      await fetch(`${API}/auth/refresh`, { method: 'POST', credentials: 'include' });
      resp = await fetch(url, options);
    } catch { /* refresh failed */ }
  }
  return resp;
}

// Concurrent image loader (max 4 parallel fetches)
const imgQueue = { active: 0, max: 4, queue: [] };
function enqueueLoad(fn) {
  if (imgQueue.active < imgQueue.max) {
    imgQueue.active++;
    fn().finally(() => {
      imgQueue.active--;
      if (imgQueue.queue.length > 0) enqueueLoad(imgQueue.queue.shift());
    });
  } else {
    imgQueue.queue.push(fn);
  }
}

function LibraryThumb({ src, alt, className, selected }) {
  const ref = useRef(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let cancelled = false;

    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          obs.disconnect();
          setLoading(true);
          const slug = localStorage.getItem('activeClientSlug') || '';
          enqueueLoad(() =>
            authFetch(src.startsWith('http') ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`, {
              credentials: 'include',
              headers: { 'X-Client-Id': slug },
            })
              .then(r => { if (!r.ok) throw new Error(r.status); return r.blob(); })
              .then(blob => { if (!cancelled) setBlobUrl(URL.createObjectURL(blob)); })
              .catch(() => {})
              .finally(() => { if (!cancelled) setLoading(false); })
          );
        }
      },
      { rootMargin: '200px' }
    );
    obs.observe(el);
    return () => { cancelled = true; obs.disconnect(); };
  }, [src]);

  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  return (
    <div ref={ref} className={`${className} relative`} style={{ backgroundColor: '#e5e7eb' }}>
      {blobUrl ? (
        <img src={blobUrl} alt={alt} className="w-full h-full object-cover" draggable={false} />
      ) : loading ? (
        <div className="w-full h-full flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
        </div>
      ) : null}
      {selected && (
        <div className="absolute inset-0 bg-blue-500/30 ring-2 ring-inset ring-blue-500 flex items-center justify-center">
          <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center shadow">
            <Check className="w-4 h-4 text-white" strokeWidth={3} />
          </div>
        </div>
      )}
    </div>
  );
}

const DEFAULT_CATEGORIES = [
  'Runway & Aircraft', 'Terminal & Facilities', 'Staff & Team',
  'Passengers & Travel', 'Scenic & Sunsets', 'Events & Community', 'General'
];

export function ImageLibrary({ isOpen, onClose, onSelectImage, multiSelect = false }) {
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('General');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showAllClients, setShowAllClients] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [sortBy, setSortBy] = useState('newest'); // 'newest' | 'least-used'
  const { toast } = useToast();
  const { activeClient } = useClient();

  const loadImages = useCallback(async (allClients = false) => {
    setIsLoading(true);
    try {
      // Auto-refresh usage counts before loading images
      try {
        await axios.post(`${API}/library/sync-usage`, {}, { withCredentials: true });
      } catch { /* non-blocking — stale counts are acceptable */ }

      const qs = allClients ? '?all_clients=true' : '';
      const resp = await axios.get(`${API}/library/images${qs}`, { withCredentials: true });
      const data = resp.data;
      setImages(data.images || []);
      const uniqueCategories = [...new Set(data.images?.map(img => img.category) || [])];
      setCategories([...new Set([...DEFAULT_CATEGORIES, ...uniqueCategories])]);
    } catch (error) {
      console.error('Failed to load images:', error);
      toast({ title: 'Failed to load library', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set());
      loadImages(showAllClients);
    }
  }, [isOpen, loadImages, showAllClients, activeClient]);

  const handleToggleAllClients = () => {
    const next = !showAllClients;
    setShowAllClients(next);
    setSelectedCategory('All');
    setSearchQuery('');
    setImages([]);
    loadImages(next);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setIsUploading(true);
    let successCount = 0;
    let dupeCount = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', uploadCategory);
        await axios.post(`${API}/library/upload`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        successCount++;
      } catch (error) {
        if (error.response?.status === 409) {
          dupeCount++;
          const msg = error.response.data?.message || 'Duplicate image';
          toast({ title: `Skipped: ${file.name}`, description: msg, variant: 'destructive' });
        } else {
          console.error(`Failed to upload ${file.name}:`, error);
        }
      }
    }
    const parts = [`${successCount} of ${files.length} images uploaded to ${uploadCategory}`];
    if (dupeCount > 0) parts.push(`${dupeCount} duplicate${dupeCount > 1 ? 's' : ''} skipped`);
    toast({ title: 'Upload complete', description: parts.join('. ') });
    setIsUploading(false);
    setShowUploadForm(false);
    loadImages(showAllClients);
  };

  const handleImageClick = (image) => {
    if (multiSelect) {
      setSelected(prev => {
        const next = new Set(prev);
        if (next.has(image.id)) {
          next.delete(image.id);
        } else {
          next.add(image.id);
        }
        return next;
      });
    } else {
      // Single-select: pick and close
      if (onSelectImage) onSelectImage(image);
      onClose();
    }
  };

  const handleDone = () => {
    if (multiSelect && onSelectImage) {
      const selectedImages = images.filter(img => selected.has(img.id));
      selectedImages.forEach(img => onSelectImage(img));
    }
    onClose();
  };

  const handleDeleteImage = async (imageId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/library/images/${imageId}`, { withCredentials: true });
      toast({ title: 'Image deleted' });
      setSelected(prev => { const next = new Set(prev); next.delete(imageId); return next; });
      loadImages(showAllClients);
    } catch {
      toast({ title: 'Failed to delete', variant: 'destructive' });
    }
  };

  const filteredImages = images.filter(img => {
    const matchesCategory = selectedCategory === 'All' || img.category === selectedCategory;
    const matchesSearch = !searchQuery ||
      img.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      img.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (showAllClients && img.client_name?.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  }).sort((a, b) => {
    if (sortBy === 'least-used') {
      const aCount = a.use_count || 0;
      const bCount = b.use_count || 0;
      if (aCount !== bCount) return aCount - bCount;
      // Tie-break: images never used before come first, then by recency of last use (oldest first)
      if (!a.last_used_at && b.last_used_at) return -1;
      if (a.last_used_at && !b.last_used_at) return 1;
      return 0;
    }
    return 0; // 'newest' — already sorted by created_at desc from backend
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[85vh] overflow-hidden flex flex-col" data-testid="image-library-modal" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#1a3d5c] flex items-center gap-2">
            <Image className="w-5 h-5 text-[#0066CC]" />
            Image Library
            {multiSelect && (
              <span className="text-sm font-normal text-gray-500 ml-2">— tap images to select multiple</span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 py-3 border-b border-[#1a3d5c]/10">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1a3d5c]/40" />
            <Input
              placeholder={showAllClients ? 'Search all client images...' : 'Search images...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 border-[#1a3d5c]/15"
              data-testid="image-search-input"
            />
          </div>
          <button
            onClick={() => setSortBy(prev => prev === 'newest' ? 'least-used' : 'newest')}
            className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all border flex items-center gap-1.5 ${
              sortBy === 'least-used' ? 'text-white border-transparent bg-emerald-600' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
            }`}
            data-testid="sort-freshness-toggle"
            title={sortBy === 'least-used' ? 'Showing least used first' : 'Sort by least used'}
          >
            <ArrowUpDown className="w-3 h-3" />
            {sortBy === 'least-used' ? 'Least Used' : 'Freshness'}
          </button>
          <button
            onClick={handleToggleAllClients}
            className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all border flex items-center gap-1.5 ${
              showAllClients ? 'text-white border-transparent bg-[#2563eb]' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
            }`}
            data-testid="library-all-clients-toggle"
          >
            All Clients
          </button>
          <Button onClick={() => setShowUploadForm(!showUploadForm)} className="btn-primary rounded-md" data-testid="upload-images-btn">
            <Plus className="w-4 h-4 mr-2" /> Upload
          </Button>
        </div>

        {/* Upload Form */}
        {showUploadForm && (
          <div className="p-4 bg-[#F8FAFC] rounded-md border border-[#1a3d5c]/10">
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1">
                <label className="text-xs font-mono uppercase tracking-wider text-[#0066CC] mb-2 block">Category</label>
                <select
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-[#1a3d5c]/15 bg-white text-[#1a3d5c]"
                  data-testid="upload-category-select"
                >
                  {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div>
                <label className="cursor-pointer">
                  <span className="btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0066CC] text-white font-semibold">
                    <Upload className="w-4 h-4" />
                    {isUploading ? 'Uploading...' : 'Choose Files'}
                  </span>
                  <input type="file" accept="image/*,video/mp4,video/quicktime,.mov,.heic,.heif" multiple onChange={handleFileUpload} disabled={isUploading} className="hidden" data-testid="image-file-input" />
                </label>
              </div>
            </div>
            <p className="text-xs text-[#1a3d5c]/50 mt-2">Upload multiple images or videos. Supported: JPG, PNG, GIF, WebP, HEIC, MP4, MOV</p>
          </div>
        )}

        {/* Category Tabs */}
        <div className="flex gap-2 overflow-x-auto py-2 scrollbar-hide">
          <button
            onClick={() => setSelectedCategory('All')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
              selectedCategory === 'All' ? 'bg-[#0066CC] text-white' : 'bg-[#F8FAFC] text-[#1a3d5c]/70 hover:bg-[#0066CC]/10'
            }`}
            data-testid="category-all"
          >
            All ({images.length})
          </button>
          {categories.map(cat => {
            const count = images.filter(img => img.category === cat).length;
            if (count === 0) return null;
            return (
              <button key={cat} onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === cat ? 'bg-[#0066CC] text-white' : 'bg-[#F8FAFC] text-[#1a3d5c]/70 hover:bg-[#0066CC]/10'
                }`}
                data-testid={`category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat} ({count})
              </button>
            );
          })}
        </div>

        {/* Image Grid */}
        <div className="flex-1 overflow-y-auto py-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-3 border-[#0066CC] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="text-center py-12">
              <Folder className="w-12 h-12 text-[#1a3d5c]/20 mx-auto mb-3" />
              <p className="text-[#1a3d5c]/60 mb-2">{images.length === 0 ? 'No images yet' : 'No images match your filter'}</p>
              <p className="text-sm text-[#1a3d5c]/40">Upload images to build your library</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="image-grid">
              {filteredImages.map(image => (
                <div
                  key={image.id}
                  onClick={() => handleImageClick(image)}
                  className={`group relative aspect-square rounded-md overflow-hidden cursor-pointer border-2 transition-colors ${
                    selected.has(image.id) ? 'border-blue-500' : 'border-transparent hover:border-[#0066CC]'
                  }`}
                  data-testid={`library-image-${image.id}`}
                >
                  <LibraryThumb
                    src={image.thumbnail_url || image.url}
                    alt={image.name}
                    className="w-full h-full"
                    selected={selected.has(image.id)}
                  />
                  {/* Overlay (only when not selected) */}
                  {!selected.has(image.id) && (
                    <div className="absolute inset-0 bg-[#1a3d5c]/0 group-hover:bg-[#1a3d5c]/30 transition-colors flex items-center justify-center">
                      {multiSelect ? (
                        <CheckSquare className="w-8 h-8 text-white opacity-0 group-hover:opacity-80 transition-opacity" />
                      ) : (
                        <Check className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </div>
                  )}
                  {/* Usage badge */}
                  {image.use_count > 0 && (() => {
                    const lastUsed = image.last_used_at ? new Date(image.last_used_at) : null;
                    const twelveMonthsAgo = new Date();
                    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
                    const isRecent = lastUsed && lastUsed >= twelveMonthsAgo;
                    return (
                      <span
                        className={`absolute top-2 left-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                          isRecent
                            ? 'bg-amber-500 text-white'
                            : 'bg-gray-500/80 text-white'
                        }`}
                        title={lastUsed ? `Last used: ${lastUsed.toLocaleDateString()}` : `Used ${image.use_count} time(s)`}
                        data-testid={`usage-badge-${image.id}`}
                      >
                        {isRecent ? `Used ×${image.use_count}` : 'Used 12+ mo'}
                      </span>
                    );
                  })()}
                  {/* Info */}
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
                    <p className="text-xs text-white truncate">{image.name}</p>
                    <p className="text-[10px] text-white/70">{image.category}{showAllClients && image.client_name ? ` · ${image.client_name}` : ''}</p>
                  </div>
                  {/* Delete */}
                  <button
                    onClick={(e) => handleDeleteImage(image.id, e)}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    data-testid={`delete-image-${image.id}`}
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="pt-3 border-t border-[#1a3d5c]/10 flex justify-between items-center">
          <p className="text-xs text-[#1a3d5c]/50">
            {filteredImages.length} images {selectedCategory !== 'All' && `in ${selectedCategory}`}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} className="border-[#1a3d5c]/30" data-testid="library-cancel-btn">
              Cancel
            </Button>
            {multiSelect && (
              <Button
                onClick={handleDone}
                disabled={selected.size === 0}
                className="bg-[#0066CC] hover:bg-[#0055aa] text-white"
                data-testid="library-done-btn"
              >
                <CheckSquare className="w-4 h-4 mr-1.5" />
                Done ({selected.size} selected)
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
